import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    styled,
    useTheme,
    IconButton,
    Tooltip,
    Paper,
    Chip,
    Typography
} from "@mui/material";
import { Check, Close, Edit } from "@mui/icons-material";
import { useState } from "react";
import UpdateMemberModal from "./UpdateMemberModal";
import { toast } from "react-toastify";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import ConfirmationDialog from "components/ConfirmationDialog";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: "16px 20px",
    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    whiteSpace: "nowrap",
    "&.header": {
        fontWeight: 600,
        fontSize: "0.875rem",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:hover": {
        backgroundColor: theme.palette.mode === 'dark' 
            ? "rgba(255, 255, 255, 0.05)" 
            : "rgba(0, 0, 0, 0.05)",
    },
}));

const SelfRegisteredTable = ({ members, searchQuery, onMemberStatusChange, currentTab }) => {
    const theme = useTheme();
    const [openModal, setOpenModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [memberToReject, setMemberToReject] = useState(null);
    const [isRejecting, setIsRejecting] = useState(false);

    const filteredMembers = members.filter(member => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        return (
            member.full_name?.toLowerCase().includes(searchLower) ||
            member.company_name?.toLowerCase().includes(searchLower) ||
            member.email?.toLowerCase().includes(searchLower)
        );
    });

    const handleEdit = (member) => {
        setSelectedMember(member);
        setOpenModal(true);
    };

    const handleRejectClick = (member) => {
        setMemberToReject(member);
        setOpenConfirmDialog(true);
    };

    const handleRejectConfirm = async () => {
        if (!memberToReject) return;
        
        setIsRejecting(true);
        try {
            const payload = {
                id: memberToReject.id,
            }
            const response = await apiService.put(
                `${API_ENDPOINTS.PUT_reject_self_registered_members}`,
                payload
            );
          
            if (response?.success) {
                toast.success("Member rejected successfully");
                onMemberStatusChange();
                setOpenConfirmDialog(false);
                setMemberToReject(null);
            }
        } catch (error) {
            console.error("API error:", error);
            toast.error("Failed to reject member");
        } finally {
            setIsRejecting(false);
        }
    };

    return (
        <Box sx={{ width: "100%", p: 2 }}>
            {filteredMembers.length > 0 ? (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell className="header">Name</StyledTableCell>
                                <StyledTableCell className="header">Company</StyledTableCell>
                                <StyledTableCell className="header">Email</StyledTableCell>
                                <StyledTableCell className="header">Contact</StyledTableCell>
                                <StyledTableCell className="header">City</StyledTableCell>
                                <StyledTableCell className="header" align="center">Status</StyledTableCell>
                                {currentTab === 1 && (
                                    <StyledTableCell className="header" align="center">Actions</StyledTableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredMembers.map((member) => (
                                <StyledTableRow key={member.id}>
                                    <StyledTableCell>{member.full_name}</StyledTableCell>
                                    <StyledTableCell>{member.company_name}</StyledTableCell>
                                    <StyledTableCell>{member.email}</StyledTableCell>
                                    <StyledTableCell>{member.contact_no}</StyledTableCell>
                                    <StyledTableCell>{member.city}</StyledTableCell>
                                    <StyledTableCell align="center">
                                        <Chip
                                            label={
                                                member.rejected_by_admin 
                                                    ? "Rejected" 
                                                    : member.accepted_by_admin 
                                                        ? "Accepted" 
                                                        : "Pending"
                                            }
                                            color={
                                                member.rejected_by_admin 
                                                    ? "error" 
                                                    : member.accepted_by_admin 
                                                        ? "success" 
                                                        : "warning"
                                            }
                                            size="small"
                                        />
                                    </StyledTableCell>
                                    {currentTab === 1 && (
                                        <StyledTableCell align="center">
                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                <Tooltip title="Accept & Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEdit(member)}
                                                        sx={{
                                                            color: theme.palette.success.main,
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.success.main + '20'
                                                            }
                                                        }}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Reject">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRejectClick(member)}
                                                        sx={{
                                                            color: theme.palette.error.main,
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.error.main + '20'
                                                            }
                                                        }}
                                                    >
                                                        <Close fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </StyledTableCell>
                                    )}
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box 
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8
                    }}
                >
                    
                    
                    <Typography 
                        variant="h6" 
                        color="text.secondary"
                        align="center"
                    >
                        {searchQuery 
                            ? "No members found matching your search"
                            : currentTab === 1 
                                ? "No pending members"
                                : currentTab === 2
                                    ? "No accepted members"
                                    : "No rejected members"
                        }
                    </Typography>
                </Box>
            )}

            <UpdateMemberModal
                open={openModal}
                onClose={() => {
                    setOpenModal(false);
                    setSelectedMember(null);
                }}
                member={selectedMember}
                onSuccess={() => {
                    onMemberStatusChange();
                    setOpenModal(false);
                }}
            />

            <ConfirmationDialog
                open={openConfirmDialog}
                onClose={() => {
                    setOpenConfirmDialog(false);
                    setMemberToReject(null);
                }}
                onConfirm={handleRejectConfirm}
                title="Confirm Rejection"
                message={`Are you sure you want to reject ${memberToReject?.full_name}? This action cannot be undone.`}
                confirmButtonText="Reject"
                confirmButtonColor="error"
                isLoading={isRejecting}
            />
        </Box>
    );
};

export default SelfRegisteredTable; 